import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  IconButton,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Trash2 } from 'lucide-react';
import { useDeleteSyncHistory } from 'queries/useSynchronizeHistory';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SyncDocumentHistory } from 'services/synchronization/synchronization.type';

/**
 * Row action for the Synchronization history table: delete (unsync) a single synced folder
 * without removing the whole SharePoint connection. Mirrors ConnectionActionButtons' dialog.
 */
function SyncActionButtons({ info }: { info: SyncDocumentHistory }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<any>();
  const { mutate: deleteSync, isPending: isDeleting } = useDeleteSyncHistory();

  const handleDelete = () => {
    onClose();
    deleteSync(info.id, {
      onSuccess: () =>
        toast({
          title: t('common.success'),
          description: t('synchronization.deleteSyncSuccess', {
            defaultValue: 'Synchronization deleted.',
          }),
          status: 'success',
          duration: 3000,
          isClosable: true,
        }),
      onError: () =>
        toast({
          title: t('common.error'),
          // BE only lets the user who triggered the sync delete it.
          description: t('synchronization.deleteSyncFailed', {
            defaultValue:
              'Could not delete this synchronization. Only the user who triggered it can delete it.',
          }),
          status: 'error',
          duration: 4000,
          isClosable: true,
        }),
    });
  };

  const label = t('synchronization.deleteSync', { defaultValue: 'Delete synchronization' });

  return (
    <>
      <Tooltip label={label}>
        <IconButton
          color='text.muted'
          aria-label={label}
          icon={<Trash2 size={15} />}
          isLoading={isDeleting}
          onClick={onOpen}
          size='ds-sm'
          variant='ds-ghost'
        />
      </Tooltip>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        returnFocusOnClose={false}
      >
        <AlertDialogOverlay bg='blackAlpha.600' backdropFilter='blur(3px)'>
          <AlertDialogContent
            pos='relative'
            bg='bg.surfaceRaised'
            borderWidth='1px'
            borderColor='border.default'
            borderRadius='xl'
            shadow='ds_lg'
          >
            <AlertDialogHeader
              color='text.default'
              fontFamily='heading'
              fontSize='lg'
              fontWeight={600}
            >
              {label}
            </AlertDialogHeader>

            <AlertDialogBody color='text.muted' fontSize='sm'>
              {t('synchronization.deleteSyncConfirmation', {
                defaultValue:
                  'This stops syncing "{{folder}}". The connection and its other synced folders are not affected.',
                folder: info.spo_folder || info.spo_site,
              })}
            </AlertDialogBody>

            <AlertDialogFooter gap={3}>
              <Button ref={cancelRef} onClick={onClose} size='ds-md' variant='ds-ghost'>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleDelete} size='ds-md' variant='ds-danger'>
                {t('common.delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export default SyncActionButtons;
